import * as React from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Table as TanStackTable,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { DataTableColumnVisibility } from "@/components/ui/data-table-column-visibility";
import { DataTablePaginationControls } from "@/components/ui/data-table-pagination-controls";
import type { DataTableSelectionMeta } from "@/components/ui/data-table-row-selection";
import { type DropdownMenuContent } from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

function columnAlignDataAttr(
  meta: unknown
): "center" | "end" | undefined {
  const align = (meta as { align?: string } | undefined)?.align;
  if (align === "center" || align === "end") return align;
  return undefined;
}

function columnHeadCellDataAttrs(
  meta: unknown
): Record<string, string> | undefined {
  const m = meta as { simulatorMetricsDeltaCol?: boolean } | undefined;
  if (!m?.simulatorMetricsDeltaCol) return undefined;
  return { "data-simulator-metrics-delta-col": "" };
}

function isInteractiveRowClickTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      [
        "button",
        "a",
        "input",
        "textarea",
        "select",
        '[role="checkbox"]',
        '[role="menuitem"]',
        '[role="menu"]',
        "[data-no-row-click]",
        '[data-slot="checkbox"]',
        '[data-slot="data-table-button"]',
      ].join(", ")
    )
  );
}

const ROW_CLICK_MOVE_THRESHOLD_PX = 8;

function DataTablePagination<TData>({
  table,
}: {
  table: TanStackTable<TData>;
}) {
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  return (
    <DataTablePaginationControls
      pageIndex={pageIndex}
      pageCount={pageCount}
      onPageIndexChange={(index) => table.setPageIndex(index)}
      canPreviousPage={table.getCanPreviousPage()}
      canNextPage={table.getCanNextPage()}
    />
  );
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  emptyMessage?: string;
  className?: string;
  /** Optional classes on the bordered table grid wrapper (`[data-shadcn-data-table]`). */
  tableClassName?: string;
  /** Optional data attributes on the bordered table grid wrapper (`[data-shadcn-data-table]`). */
  tableDataAttributes?: Record<string, string | number | boolean | undefined>;
  /** Shown to the left of the selection summary (e.g. bulk actions). */
  toolbarActions?: React.ReactNode;
  showPagination?: boolean;
  /** Column visibility dropdown above the table (shadcn data-table pattern). */
  showColumnVisibility?: boolean;
  /** TanStack column id to filter (shadcn data-table toolbar search). */
  filterColumnId?: string;
  /** Placeholder for the filter input when `filterColumnId` is set. */
  filterPlaceholder?: string;
  /** Optional node on the left of the toolbar row (before the filter input). */
  toolbarLeading?: React.ReactNode;
  /** Optional node on the right of the toolbar row (after column visibility). */
  toolbarTrailing?: React.ReactNode;
  /** Portaled Columns menu content props (optional). */
  columnVisibilityMenuProps?: React.ComponentPropsWithoutRef<
    typeof DropdownMenuContent
  >;
  /** Trade Log: Urbanist + gray-500 on Columns trigger and menu items. */
  columnVisibilityTradeLogTypography?: boolean;
  /** When false, omits the bordered rounded wrapper around the table grid. */
  showTableBorder?: boolean;
  /** Explicit page count (e.g. when parent already computed `ceil(rows / pageSize)`). */
  pageCount?: number;
  /** Highlights a row via `data-state="active"` (e.g. market context pivot selection). */
  activeRowId?: string;
  /** Extra attributes on each body row (e.g. `data-market-context-situation` for deep links). */
  getRowDataAttributes?: (row: TData) => Record<string, string> | undefined;
  /** Remounts the active row when bumped so CSS evidence pulse animations replay. */
  evidencePulseNonce?: number;
  /** When set, renders expansion content in a full-width row directly below the matching row. */
  expandedRowId?: string;
  renderExpandedRow?: (row: TData) => React.ReactNode;
  /** When true, parent supplies row order (e.g. trade log open-first sort). */
  manualSorting?: boolean;
  /** Renders pagination below the table shell (outside bordered table chrome). */
  paginationAfterShell?: boolean;
  /** Selection summary (+ optional toolbar actions) on its own row above pagination. */
  stackedFooter?: boolean;
  /** Custom pagination controls in the footer when `showPagination` is false (e.g. server-side paging). */
  footerTrailing?: React.ReactNode;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  getRowId,
  onRowClick,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  columnVisibility,
  onColumnVisibilityChange,
  pagination,
  onPaginationChange,
  emptyMessage = "No results.",
  className,
  tableClassName,
  tableDataAttributes,
  toolbarActions,
  showPagination = true,
  showColumnVisibility = false,
  toolbarLeading,
  toolbarTrailing,
  columnVisibilityMenuProps,
  columnVisibilityTradeLogTypography = false,
  showTableBorder = true,
  pageCount: pageCountProp,
  activeRowId,
  getRowDataAttributes,
  evidencePulseNonce,
  expandedRowId,
  renderExpandedRow,
  manualSorting = false,
  paginationAfterShell = false,
  stackedFooter = false,
  footerTrailing,
}: DataTableProps<TData, TValue>) {
  const [internalColumnVisibility, setInternalColumnVisibility] =
    React.useState<VisibilityState>({});
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);

  const resolvedColumnVisibility = columnVisibility ?? internalColumnVisibility;
  const resolvedOnColumnVisibilityChange =
    onColumnVisibilityChange ?? setInternalColumnVisibility;
  const resolvedPagination = pagination ?? internalPagination;
  const resolvedOnPaginationChange = onPaginationChange ?? setInternalPagination;
  const resolvedSorting = sorting ?? internalSorting;
  const resolvedOnSortingChange = onSortingChange ?? setInternalSorting;

  const rowPressRef = React.useRef<{ rowId: string; x: number; y: number } | null>(
    null
  );
  const rowActivatedByPointerRef = React.useRef(false);

  // Shift+range selection for createDataTableSelectColumn (and any consumer of meta.selection).
  const selectionInternalsRef = React.useRef({
    anchor: null as string | null,
    shiftHandled: false,
  });
  const selectionMeta = React.useMemo((): DataTableSelectionMeta => {
    return {
      getAnchor: () => selectionInternalsRef.current.anchor,
      setAnchor: (rowId) => {
        selectionInternalsRef.current.anchor = rowId;
      },
      markShiftHandled: () => {
        selectionInternalsRef.current.shiftHandled = true;
      },
      consumeShiftHandled: () => {
        if (!selectionInternalsRef.current.shiftHandled) return false;
        selectionInternalsRef.current.shiftHandled = false;
        return true;
      },
      notePointerShift: (shiftKey) => {
        if (!shiftKey) selectionInternalsRef.current.shiftHandled = false;
      },
    };
  }, []);

  React.useEffect(() => {
    selectionInternalsRef.current.anchor = null;
    selectionInternalsRef.current.shiftHandled = false;
  }, [resolvedPagination.pageIndex]);

  React.useEffect(() => {
    const sel = rowSelection ?? {};
    const anySelected = Object.values(sel).some(Boolean);
    if (!anySelected) {
      selectionInternalsRef.current.anchor = null;
    }
  }, [rowSelection]);

  const table = useReactTable({
    data,
    columns,
    getRowId,
    enableRowSelection,
    onSortingChange: resolvedOnSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: resolvedOnColumnVisibilityChange,
    onRowSelectionChange,
    onPaginationChange: resolvedOnPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
    getFilteredRowModel: getFilteredRowModel(),
    ...(pageCountProp !== undefined ? { pageCount: pageCountProp } : {}),
    autoResetPageIndex: false,
    meta: {
      selection: selectionMeta,
    },
    state: {
      sorting: resolvedSorting,
      columnFilters,
      columnVisibility: resolvedColumnVisibility,
      rowSelection: rowSelection ?? {},
      pagination: resolvedPagination,
    },
  });

  const selectedCount = table.getFilteredSelectedRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;
  const showFooter = showPagination || enableRowSelection || Boolean(footerTrailing);
  const showInlineFooter = showFooter && !paginationAfterShell;
  const showDetachedFooter = showFooter && paginationAfterShell;
  const footerToolbarActions = stackedFooter ? toolbarActions : null;
  const headerToolbarActions = stackedFooter ? null : toolbarActions;

  const selectionSummary = enableRowSelection ? (
    <div data-data-table-selection-summary="">
      {selectedCount} of {filteredCount} row(s) selected.
    </div>
  ) : null;

  const footer = showFooter ? (
    stackedFooter ? (
      <div
        data-shadcn-data-table-footer=""
        data-stacked-footer=""
        className="flex flex-col items-stretch gap-2 py-4"
      >
        {enableRowSelection || footerToolbarActions ? (
          <div className="flex w-full flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            {selectionSummary}
            {footerToolbarActions ? (
              <div className="flex shrink-0 items-center gap-2">{footerToolbarActions}</div>
            ) : null}
          </div>
        ) : null}
        {showPagination ? (
          <div className="flex w-full justify-end">
            <DataTablePagination table={table} />
          </div>
        ) : (
          footerTrailing
        )}
      </div>
    ) : (
      <div
        data-shadcn-data-table-footer=""
        className={cn(
          "flex items-center gap-2 py-4",
          enableRowSelection ? "justify-between" : "justify-end",
        )}
      >
        <div className="flex flex-1 items-center gap-2 text-sm text-muted-foreground">
          {selectionSummary}
        </div>
        {showPagination ? (
          <DataTablePagination table={table} />
        ) : (
          footerTrailing
        )}
      </div>
    )
  ) : null;

  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const showToolbar =
    showColumnVisibility ||
    Boolean(toolbarLeading) ||
    Boolean(toolbarTrailing) ||
    Boolean(headerToolbarActions);

  return (
    <>
      <div
        data-shadcn-data-table-shell
        className={cn("w-full text-foreground", className)}
      >
      {showToolbar ? (
        <div className="flex w-full items-center gap-2 pb-4">
          {toolbarLeading}
          {showColumnVisibility || toolbarTrailing || headerToolbarActions ? (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {headerToolbarActions}
              {showColumnVisibility ? (
                <DataTableColumnVisibility
                  table={table}
                  menuContentProps={columnVisibilityMenuProps}
                  tradeLogTypography={columnVisibilityTradeLogTypography}
                />
              ) : null}
              {toolbarTrailing}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        data-shadcn-data-table
        {...tableDataAttributes}
        {...(!showTableBorder ? { "data-shadcn-data-table-borderless": "" } : {})}
        className={cn(
          "overflow-hidden bg-background",
          showTableBorder && "rounded-[6px] border border-border",
          tableClassName,
        )}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    data-column-align={columnAlignDataAttr(header.column.columnDef.meta)}
                    {...columnHeadCellDataAttrs(header.column.columnDef.meta)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const rowId = getRowId?.(row.original);
                const isExpanded = Boolean(
                  expandedRowId && rowId && expandedRowId === rowId && renderExpandedRow,
                );
                return (
                  <React.Fragment
                    key={
                      evidencePulseNonce &&
                      activeRowId &&
                      rowId === activeRowId
                        ? `${row.id}-evidence-${evidencePulseNonce}`
                        : row.id
                    }
                  >
                    <TableRow
                      data-state={
                        row.getIsSelected()
                          ? "selected"
                          : activeRowId && rowId === activeRowId
                            ? "active"
                            : undefined
                      }
                      className={cn(onRowClick && "cursor-pointer select-none")}
                      onPointerDown={
                        onRowClick
                          ? (e) => {
                              if (e.button !== 0) return;
                              rowPressRef.current = {
                                rowId: row.id,
                                x: e.clientX,
                                y: e.clientY,
                              };
                              rowActivatedByPointerRef.current = false;
                            }
                          : undefined
                      }
                      onPointerUp={
                        onRowClick
                          ? (e) => {
                              if (e.button !== 0) return;
                              const press = rowPressRef.current;
                              rowPressRef.current = null;
                              if (!press || press.rowId !== row.id) return;
                              const dx = Math.abs(e.clientX - press.x);
                              const dy = Math.abs(e.clientY - press.y);
                              if (
                                dx > ROW_CLICK_MOVE_THRESHOLD_PX ||
                                dy > ROW_CLICK_MOVE_THRESHOLD_PX
                              ) {
                                return;
                              }
                              if (isInteractiveRowClickTarget(e.target)) return;
                              rowActivatedByPointerRef.current = true;
                              onRowClick(row.original);
                            }
                          : undefined
                      }
                      onClick={
                        onRowClick
                          ? (e) => {
                              if (rowActivatedByPointerRef.current) {
                                rowActivatedByPointerRef.current = false;
                                e.preventDefault();
                                return;
                              }
                              if (isInteractiveRowClickTarget(e.target)) return;
                              onRowClick(row.original);
                            }
                          : undefined
                      }
                      {...getRowDataAttributes?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          data-column-align={columnAlignDataAttr(cell.column.columnDef.meta)}
                          {...columnHeadCellDataAttrs(cell.column.columnDef.meta)}
                          onClick={
                            cell.column.id === "select" ||
                            cell.column.id === "actions" ||
                            cell.column.id === "pick" ||
                            cell.column.id === "details" ||
                            cell.column.id === "simulatorPick" ||
                            cell.column.id === "drilldown" ||
                            cell.column.id === "viewPolicy" ||
                            cell.column.id === "viewPine" ||
                            cell.column.id === "compose" ||
                            cell.column.id === "simulation" ||
                            cell.column.id === "report" ||
                            cell.column.id === "current"
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded ? (
                      <TableRow
                        data-expanded-detail-row=""
                        className="!bg-transparent hover:!bg-transparent"
                      >
                        <TableCell
                          colSpan={Math.max(visibleColumnCount, 1)}
                          className="max-w-0"
                        >
                          {renderExpandedRow!(row.original)}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={Math.max(visibleColumnCount, 1)}
                  className="h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {showInlineFooter ? footer : null}
      </div>
      {showDetachedFooter ? footer : null}
    </>
  );
}

export type { TanStackTable };
